import { HelixIcon } from '@helix/helix-icon';
import { x } from '@helix/helix-icon/outlined';
import React, {
    type ComponentProps,
    createContext,
    type PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    StyledModal,
    StyledModalBody,
    StyledModalContent,
    StyledModalFooter,
    StyledModalHeader,
    StyledOverlay
} from './Modal.styles';

const ModalContext = createContext<{
    hideCloseButton?: boolean;
    autoFocusCloseButton?: boolean;
    onClose?: () => void;
}>({
    hideCloseButton: false,
    autoFocusCloseButton: true,
    onClose: () => {
        /* noop */
    }
});

export interface ModalProps extends ComponentProps<'div'> {
    show?: boolean;
    size?: 'default' | 'lg';
    dataLwtId?: string;
    hideCloseButton?: boolean;
    autoFocusCloseButton?: boolean;
    onCloseAnimationComplete?: () => void;
    onClose?: () => void;
}

export function Modal({
    show,
    size = 'default',
    dataLwtId,
    hideCloseButton = false,
    autoFocusCloseButton = true,
    onCloseAnimationComplete,
    onClose,
    children,
    className,
    ...props
}: ModalProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(show ?? false);
    const [modalLargerThanOverlay, setModalLargerThanOverlay] = useState(false);

    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!show && !visible) return;

        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCloseRef.current?.();
            }

            if (e.key === 'Tab' && contentRef.current) {
                const focusableElements = contentRef.current.querySelectorAll<HTMLElement>(
                    "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
                );

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    // Reverse tabbing
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else {
                    // Forward tabbing
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            }
        };

        document.body.addEventListener('keydown', handleKeydown);
        return () => {
            document.body.removeEventListener('keydown', handleKeydown);
        };
    }, [show, visible]);

    useEffect(() => {
        if (!show && !visible) return;

        const handleResize = () => {
            if (contentRef.current && overlayRef.current) {
                const modalHeight = contentRef.current.clientHeight;
                const overlayHeight = overlayRef.current.clientHeight;
                setModalLargerThanOverlay(modalHeight > overlayHeight);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [show, visible]);

    useEffect(() => {
        if (show) {
            setVisible(true);
        }
    }, [show]);

    if (!visible && !show) return null;

    return (
        <ModalContext.Provider
            value={{
                onClose,
                hideCloseButton,
                autoFocusCloseButton
            }}
        >
            <StyledModal modalLargerThanOverlay={modalLargerThanOverlay}>
                <StyledOverlay
                    ref={overlayRef}
                    className={`helix-modal-overlay ${show ? 'animate-in' : 'animate-out'}`}
                    onAnimationEnd={() => {
                        if (!show) {
                            setVisible(false);
                            onCloseAnimationComplete?.();
                        }
                    }}
                />
                <StyledModalContent
                    size={size}
                    ref={contentRef}
                    role='dialog'
                    aria-modal='true'
                    data-lwt-id={dataLwtId}
                    className={`helix-modal helix-modal-content ${show ? 'animate-in' : 'animate-out'} ${
                        className ? className : ''
                    }`}
                    {...props}
                >
                    {children}
                </StyledModalContent>
            </StyledModal>
        </ModalContext.Provider>
    );
}

export function ModalHeader({ children, ...props }: PropsWithChildren<unknown>) {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const { onClose, autoFocusCloseButton, hideCloseButton } = useContext(ModalContext);

    useEffect(() => {
        if (autoFocusCloseButton && !hideCloseButton) {
            closeButtonRef.current?.focus();
        }
    }, [autoFocusCloseButton, hideCloseButton]);

    return (
        <StyledModalHeader {...props}>
            <div className='helix-modal__header-content'>{children}</div>
            {!hideCloseButton && (
                <button
                    className='helix-btn helix-btn--ghost helix-btn--small helix-btn--icon-btn'
                    aria-label='Close'
                    onClick={onClose}
                    ref={closeButtonRef}
                    type='button'
                    data-helix-close='true'
                >
                    <HelixIcon icon={x} />
                </button>
            )}
        </StyledModalHeader>
    );
}

export function ModalBody({ children, ...props }: PropsWithChildren<unknown>) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const [bodyHasScrollbar, setBodyHasScrollbar] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    useEffect(() => {
        const checkForScrollbar = () => {
            if (bodyRef.current) {
                setBodyHasScrollbar(bodyRef.current.scrollHeight > bodyRef.current.clientHeight);
            }
        };
        checkForScrollbar();
        window.addEventListener('resize', checkForScrollbar);
        return () => {
            window.removeEventListener('resize', checkForScrollbar);
        };
    }, [children]);

    useEffect(() => {
        const handleScroll = () => {
            if (bodyRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
                setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
            }
        };
        const body = bodyRef.current;
        if (body) {
            body.addEventListener('scroll', handleScroll);
            // Initial check
            handleScroll();
        }
        return () => {
            if (body) {
                body.removeEventListener('scroll', handleScroll);
            }
        };
    }, [bodyHasScrollbar, children]);

    return (
        <div style={{ position: 'relative', overflow: 'hidden', display: 'flex' }}>
            <StyledModalBody {...props} ref={bodyRef}>
                {children}
            </StyledModalBody>
            {bodyHasScrollbar && !atBottom && <div className='helix-modal__gradient' />}
        </div>
    );
}

export function ModalFooter({ children, ...props }: PropsWithChildren<unknown>) {
    return <StyledModalFooter {...props}>{children}</StyledModalFooter>;
}
